import React, {FC, createContext, useMemo, useEffect} from 'react'
import {useSelector} from 'react-redux'
import {select_auth_isAuthenticated} from 'redux/auth/selectors'
import {useAppDispatch} from 'redux/config'
import {authAC} from 'redux/auth/auth.duck'
import {AuthClientType, isAuthenticated, registerTokenEvents, subscribeToAuthChanges} from 'helpers/security'
import {useEffectOnce} from 'usehooks-ts'

const SecurityContext = createContext<AuthClientType>({})
SecurityContext.displayName = 'Security Context'

const SecurityContextProvider: FC<{authClient, children}> = ({authClient, children}) => {
  const dispatch = useAppDispatch()

  const contextValue = useMemo(() => authClient, [authClient])

  // subscribe for authentication state changes and token expire/renew events
  useEffectOnce(() => {
    subscribeToAuthChanges(authClient, dispatch)
    registerTokenEvents(authClient)
  })

  const isLoggedInAcrossBrowser = isAuthenticated()

  // clear store authentication if okta token no longer exists in local storage (e.g. they logged out in another tab)
  useEffect(() => {
    if (!isLoggedInAcrossBrowser) {
      dispatch(authAC.clientCleared())
      return
    }
  }, [isLoggedInAcrossBrowser, dispatch])

  // do not render app if not authenticated during redirects to and from OKTA
  const isAuth = useSelector(select_auth_isAuthenticated)
  if (!isAuth) return null

  return (
    <SecurityContext.Provider value={contextValue}>
      {children}
    </SecurityContext.Provider>
  )
}

export default SecurityContextProvider
